import { computed, reactive, watch } from 'vue'
import api from '@/api'
import searchQueryState from './SearchQuery'
import searchConfiguration from './SearchConfiguration'

const searchResultState = reactive({
  loading: false,
  allItemsLoaded: false,
  totalCount: 0,
  hasResults: false,
  items: [],
  itemsByCategory: [],
  termsFacet: [],
  searchResults: {},
  trackId: ''
})

export default {
  useSearchResult() {
    const state = searchResultState

    const {
      searchQuery, groupByCategories, incrementPage, resetPageNumber
    } = searchQueryState.searchQueryUse()
    const { take } = searchQuery

    const { getActiveConfig } = searchConfiguration.searchConfigurationUse()
    const { combinedCategories } = getActiveConfig()

    function collectItemsByCategory() {
      if (!groupByCategories) {
        return []
      }

      return combinedCategories
        .map((category) => {
          if (category.hidden) {
            return null
          }
          const items = state.items.filter((item) => item.categories.length && item.categories.includes(category.uid))
          const machedFacet = state.termsFacet.find((facet) => category.uid === facet.term)
          if (!machedFacet) {
            return null
          }
          return { items, category }
        })
        .filter((facet) => facet != null && facet.items.length > 0)
    }

    async function fetchData() {
      state.items = []
      state.itemsByCategory = []
      state.totalCount = ''
      state.allItemsLoaded = false
      state.hasResults = false
      resetPageNumber()
      const response = await api.getSearchItems(searchQuery, { skip: 0 })
      state.searchResults = response.data.searchResults
      const activeSearchResult = state.searchResults
        .find((x) => x.type === searchQuery.searchType)
      state.totalCount = activeSearchResult.totalCount
      state.termsFacet = activeSearchResult.termsFacet
      state.items = activeSearchResult.items
      state.itemsByCategory = collectItemsByCategory()
      state.trackId = activeSearchResult.trackId
      state.hasResults = state.searchResults.some(({ totalCount }) => totalCount > 0)

      if (state.items.length >= state.totalCount) {
        state.allItemsLoaded = true
      }
    }

    async function fetchExtraItems() {
      const skip = state.items.length

      const searchRequest = {
        language: searchQuery.language,
        skip,
        take,
        page: searchQuery.page,
        activeCategories: searchQuery.activeCategories,
        terms: searchQuery.terms,
        sorting: searchQuery.sorting
      }

      if (searchQuery.searchType === 'product') {
        const response = await api.getProducts(searchRequest)
        state.items.push(...response.data.items)
        state.itemsByCategory = collectItemsByCategory()
      }

      if (state.items.length >= state.totalCount) {
        state.allItemsLoaded = true
      }
    }

    async function search() {
      if (!state.loading) {
        state.loading = true
        fetchData().then(() => {
          state.loading = false
        })
      }
    }

    async function searchMore() {
      if (state.totalCount === state.items.length || state.allItemsLoaded) {
        return
      }

      if (!state.loading) {
        incrementPage()
        state.loading = true
        fetchExtraItems().then(() => {
          state.loading = false
        })
      }
    }
    watch(
      () => [searchQuery.updated],
      () => {
        if (!state.loading && searchQuery.searchType) {
          search()
        }
      },
      {
        immediate: true
      }
    )

    return {
      allItemsLoaded: computed(() => state.allItemsLoaded),
      loading: computed(() => state.loading),
      searchResults: computed(() => state.searchResults),
      totalCount: computed(() => state.totalCount),
      items: computed(() => state.items),
      itemsByCategory: computed(() => state.itemsByCategory),
      termsFacet: computed(() => state.termsFacet),
      trackId: computed(() => state.trackId),
      hasResults: computed(() => state.hasResults),
      search,
      searchMore
    }
  }
}
